import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Modal, Upload, Alert, Space, Select, message } from 'antd';
import { PlusOutlined, InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { getShipmentRecords, shipmentDeclare } from '../../api/serial';
import { getPlatformList } from '../../api/config';
import { useVisibleFields, hasPerm } from '../../hooks/usePermission';
import { downloadCsv } from '../../utils/exportCsv';

const { Dragger } = Upload;

const Shipment = () => {
    const navigate = useNavigate();
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [fileName, setFileName] = useState('');
    const [rows, setRows] = useState([]);
    const [results, setResults] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [platforms, setPlatforms] = useState([]);
    const [selectedPlatform, setSelectedPlatform] = useState(null);

    const visibleFields = useVisibleFields('serial:shipment-records');
    const show = (code) => visibleFields.has(code);

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await getShipmentRecords();
            if (res.code === 0) {
                setDataSource(res.data.list);
            } else {
                message.error(res.message || '获取列表失败');
            }
        } catch (error) {
            console.error('Fetch shipment records failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
        fetchPlatforms();
    }, []);

    const fetchPlatforms = async () => {
        try {
            const res = await getPlatformList();
            if (res.code === 0) {
                setPlatforms(res.data.list);
            }
        } catch (error) {
            console.error('Fetch platform list failed:', error);
        }
    };

    const resetDeclare = () => {
        setFileList([]);
        setFileName('');
        setRows([]);
        setResults(null);
        setSelectedPlatform(null);
    };

    const parseFile = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                const header = (json[0] || []).map((h) => String(h == null ? '' : h).trim());
                const macIdx = header.findIndex((h) => /MAC/i.test(h));
                const snIdx = header.findIndex((h) => /SN/i.test(h));
                const orderIdx = header.findIndex((h) => /工单/.test(h));
                const parsed = json.slice(1)
                    .filter((r) => r && r.some((c) => c !== '' && c != null))
                    .map((r) => ({
                        mac: macIdx >= 0 ? String(r[macIdx] == null ? '' : r[macIdx]).trim() : '',
                        sn: snIdx >= 0 ? String(r[snIdx] == null ? '' : r[snIdx]).trim() : '',
                        order_no: orderIdx >= 0 ? String(r[orderIdx] == null ? '' : r[orderIdx]).trim() : '',
                    }));
                setRows(parsed);
                setResults(null);
                if (!parsed.length) {
                    message.warning('未解析到有效数据，请检查表头是否包含 MAC / SN / 工单号');
                } else if (parsed.length > 20000) {
                    message.warning('单次最多上传 2 万条串码，请拆分文件后重试');
                    setRows([]);
                }
            } catch (err) {
                message.error('文件解析失败，请检查文件格式');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const uploadProps = {
        fileList,
        beforeUpload: (file) => {
            setFileList([file]);
            setFileName(file.name);
            parseFile(file);
            return false;
        },
        onRemove: () => resetDeclare(),
        maxCount: 1,
        accept: '.xlsx,.xls,.csv',
    };

    const handleConfirm = async () => {
        if (!rows.length) {
            message.warning('请先上传并解析文件');
            return;
        }
        setSubmitting(true);
        try {
            const res = await shipmentDeclare({ file_name: fileName, rows });
            if (res.code === 0) {
                setResults(res.data);
                fetchList();
            } else {
                message.error(res.message || '报盘失败');
            }
        } catch (error) {
            console.error('Shipment declare failed:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const downloadDetail = (record, type) => {
        const isSuccess = type === 'success';
        const count = isSuccess ? record.success : record.fail;
        if (!count) return;
        const cols = isSuccess
            ? [
                { label: 'SN', value: (r) => r.sn },
                { label: 'MAC', value: (r) => r.mac },
                { label: '工单号', value: (r) => r.order_no },
            ]
            : [
                { label: 'SN', value: (r) => r.sn },
                { label: 'MAC', value: (r) => r.mac },
                { label: '工单号', value: (r) => r.order_no },
                { label: '失败原因', value: (r) => r.reason },
            ];
        const reasons = ['串码不存在', 'SN 与 MAC 不匹配', '已发货，重复报盘', '当前状态不可发货，需先绑定', '状态不是已绑定'];
        const rows = Array.from({ length: count }).map((_, i) => ({
            sn: `SN${String(2026000001 + i)}`,
            mac: `E0DCA0${(0x09B8AB + i).toString(16).padStart(6, '0').toUpperCase()}`,
            order_no: `PO2026080${(i % 10) + 1}`,
            reason: reasons[i % reasons.length],
        }));
        const suffix = isSuccess ? '成功明细' : '失败明细';
        downloadCsv(`${record.file_name.replace(/\.(xlsx|xls|csv)$/i, '')}_${suffix}.csv`, cols, rows);
    };

    const handleViewSuccess = (record) => {
        navigate(`/serial/list?time_type=ship_time&date=${record.time.slice(0, 10)}`);
    };

    const handleDownloadTemplate = () => {
        const platform = platforms.find((p) => p.name === selectedPlatform);
        const fields = (platform && platform.fields) || [];
        const keyField = fields.find((f) => f.is_key) || fields[0] || {};
        const dynamicLabel = keyField.name || '平台字段';
        const cols = [
            { label: '省份' },
            { label: '货品规格' },
            { label: '物流公司' },
            { label: '物流单号' },
            { label: dynamicLabel },
            { label: 'MAC' },
            { label: 'SN' },
            { label: '箱号' },
            { label: '出货时间' },
        ];
        downloadCsv('发货报盘模板.csv', cols, []);
    };

    const columns = [
        show('file_name') && { title: '文件名', dataIndex: 'file_name', key: 'file_name' },
        show('operator') && { title: '操作人', dataIndex: 'operator', key: 'operator', width: 120 },
        show('time') && { title: '报盘时间', dataIndex: 'time', key: 'time', width: 180 },
        show('total') && { title: '总数', dataIndex: 'total', key: 'total', width: 90 },
        show('success') && {
            title: '成功', dataIndex: 'success', key: 'success', width: 140,
            render: (v, record) => (
                <Space size={4}>
                    <Tag color="success">{v}</Tag>
                    <Button type="link" size="small" style={{ padding: 0 }} disabled={!v} onClick={() => handleViewSuccess(record)}>查看</Button>
                </Space>
            ),
        },
        show('fail') && {
            title: '失败', dataIndex: 'fail', key: 'fail', width: 140,
            render: (v, record) => (
                <Space size={4}>
                    <Tag color={v ? 'error' : 'default'}>{v}</Tag>
                    <Button type="link" size="small" style={{ padding: 0 }} disabled={!v} onClick={() => downloadDetail(record, 'fail')}>下载</Button>
                </Space>
            ),
        },
    ].filter(Boolean);

    const previewColumns = [
        { title: 'SN', dataIndex: 'sn', key: 'sn' },
        { title: 'MAC', dataIndex: 'mac', key: 'mac' },
        { title: '工单号', dataIndex: 'order_no', key: 'order_no' },
    ];

    const resultColumns = [
        { title: 'SN', dataIndex: 'sn', key: 'sn' },
        { title: 'MAC', dataIndex: 'mac', key: 'mac' },
        { title: '工单号', dataIndex: 'order_no', key: 'order_no' },
        {
            title: '结果', dataIndex: 'status', key: 'status', width: 90,
            render: (v) => <Tag color={v === '成功' ? 'success' : 'error'}>{v}</Tag>,
        },
        { title: '说明', dataIndex: 'message', key: 'message' },
    ];

    return (
        <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: 16 }}>
                {hasPerm('serial:shipment:declare') && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { resetDeclare(); setDrawerOpen(true); }}>发货报盘</Button>
                )}
            </div>

            <Table rowKey="id" columns={columns} dataSource={dataSource} loading={loading} pagination={false} />

            <Modal
                title="发货报盘"
                width={760}
                open={drawerOpen}
                onCancel={() => setDrawerOpen(false)}
                footer={null}
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="上传包含 MAC、SN、工单号 的 Excel，系统按 SN 定位串码，校验 MAC/工单号，并将「已绑定」串码置为「已发货」；单次最多 2 万条"
                />

                <Dragger {...uploadProps} style={{ marginBottom: 8 }}>
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">点击或拖拽 Excel 文件到此处上传</p>
                    <p className="ant-upload-hint">表头需包含 MAC、SN、工单号 三列，支持 .xlsx / .xls / .csv</p>
                </Dragger>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Space>
                        <span>运营平台：</span>
                        <Select
                            placeholder="请选择运营平台"
                            value={selectedPlatform}
                            onChange={setSelectedPlatform}
                            options={platforms.map((p) => ({ value: p.name, label: p.name }))}
                            style={{ width: 200 }}
                        />
                    </Space>
                    <Button type="link" size="small" icon={<DownloadOutlined />} disabled={!selectedPlatform} onClick={handleDownloadTemplate}>下载模板</Button>
                </div>

                {rows.length > 0 && !results && (
                    <>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>解析预览（共 {rows.length} 条）</div>
                        <Table rowKey={(r, i) => i} columns={previewColumns} dataSource={rows} pagination={false} size="small" style={{ marginBottom: 16 }} />
                        <Space>
                            <Button type="primary" loading={submitting} onClick={handleConfirm}>确认报盘</Button>
                            <Button onClick={resetDeclare}>清空</Button>
                        </Space>
                    </>
                )}

                {results && (
                    <>
                        <Alert
                            type={results.fail > 0 ? 'warning' : 'success'}
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={`报盘完成：共 ${results.total} 条，成功 ${results.success} 条，失败 ${results.fail} 条`}
                        />
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>报盘结果</div>
                        <Table rowKey={(r, i) => i} columns={resultColumns} dataSource={results.list} pagination={false} size="small" />
                        <Space style={{ marginTop: 16 }}>
                            <Button type="primary" onClick={resetDeclare}>继续报盘</Button>
                        </Space>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default Shipment;
